import React, { useMemo, useState } from 'react';
import TranslationHelper from '../../../../helpers/TranslationHelper';
import DialogTitle from '../../../dialogs/DialogTitle';
import { Box, Dialog, DialogContent, ToggleButton } from '@mui/material';
import { CustomToggleButtonGroup } from '../../../Styled/StyledToggleButtonGroup';
import { Formik, FormikErrors } from 'formik';
import { DataTab } from './DataTab/DataTab';
import { Tabs, UserFormValues } from './types';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MOBILE } from '@/app/constants/dictionaries/ScreenSizeConst';
import { RolesTab } from './RolesTab/RolesTab';
import { mapUserToUserDataFormValues } from './DataTab/helpers';
import { mapUserRolesToUserRolesFormValues } from './RolesTab/helpers';
import { useAppDispatch } from '@/app/hooks';
import useToggle from '../../../../hooks/useToggle';
import { hideGlobalDialog } from '@/app/state/ui/dialog';
import { ReportsTab } from './ReportsTab/ReportsTab';
import { UserReportsFormValues } from './ReportsTab/types';
import { useUserReportAssignments } from './RolesTab/hooks/useUserReportAssignments';
import { usePrivileges } from '@/app/state/auth/index.hooks';
import { useValidationSchema } from './hooks/useValidationSchema';
import { useSnackbar } from 'notistack';
import { IUser } from '@/app/state/app/collections/dataTypes';
import {
    updateUser,
    updateUserReportAssignments,
} from '@/app/state/app/collections';
import { IRequestError } from '@/app/state/app/sync';
import { buildAssignmentDifferences } from '@/app/components/globalDialog/components/UserEditDialog/ReportsTab/helpers/buildAssignmentDifferences';

type Props = {
    user: IUser;
    onSave: () => void;
};

export const UserEditDialog = ({ user, onSave }: Props) => {
    const [selectedTab, setSelectedTab] = useState<Tabs>('data');
    const isMobile = useMediaQuery(MOBILE);
    const dispatch = useAppDispatch();
    const { isOpen, handleToggle } = useToggle(true);
    const { manageReportDefinitionsAssignment } = usePrivileges();
    const userReportAssignments = useUserReportAssignments(
        user.id,
        manageReportDefinitionsAssignment
    );
    const validationSchema = useValidationSchema();
    const { enqueueSnackbar } = useSnackbar();

    const initialValues = useMemo(() => {
        const values: UserFormValues = {
            data: mapUserToUserDataFormValues(user),
            roles: mapUserRolesToUserRolesFormValues(user.roles ?? []),
            reports: userReportAssignments,
        };

        return values;
    }, [user, userReportAssignments]);

    const title = useMemo(() => {
        let text = `${TranslationHelper.translate('Edit user')}`;
        if (user.login) {
            text += ` - ${user.login}`;
        }
        return text;
    }, [user.login]);

    const handleTabChange = (
        _: React.MouseEvent<HTMLElement>,
        newTab: Tabs | null
    ) => {
        if (newTab !== null) {
            setSelectedTab(newTab);
        }
    };

    const saveUserData = async (values: UserFormValues) => {
        return new Promise<void>((resolve, reject) => {
            dispatch(
                updateUser(
                    {
                        id: user.id,
                        name: values.data.firstName,
                        surname: values.data.lastName,
                        email: values.data.email,
                        roles: Object.keys(values.roles)
                            .filter((key) => values.roles[key] === true)
                            .map((key) => parseInt(key, 10)),
                    },
                    () => {
                        resolve();
                    },
                    (e: IRequestError) => {
                        reject(
                            e.status == 409
                                ? 'User with given e-mail address or login exists'
                                : 'User could not be updated'
                        );
                    }
                )
            );
        });
    };

    const saveUserDefinitionAssignments = async (values: UserFormValues) => {
        if (manageReportDefinitionsAssignment) {
            return new Promise<void>((resolve, reject) => {
                dispatch(
                    updateUserReportAssignments(
                        user.id,
                        buildAssignmentDifferences(
                            initialValues?.reports,
                            values.reports
                        ),
                        () => {
                            resolve();
                        },
                        () => {
                            reject('User could not be updated');
                        }
                    )
                );
            });
        }
    };

    const handleSave = async (values: UserFormValues) => {
        try {
            if (manageReportDefinitionsAssignment) {
                await Promise.all([
                    saveUserData(values),
                    saveUserDefinitionAssignments(values),
                ]);
            } else {
                await saveUserData(values);
            }
            onSave();
            handleClose();
            enqueueSnackbar(TranslationHelper.translate('User updated'), {
                variant: 'success',
            });
        } catch (error) {
            enqueueSnackbar(TranslationHelper.translate(error as string), {
                variant: 'error',
            });
        }
    };

    const handleClose = (reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(hideGlobalDialog());
        handleToggle();
    };

    const handleSaveClick =
        (
            validateForm: () => Promise<FormikErrors<UserFormValues>>,
            submitForm: () => void
        ) =>
        async () => {
            const validationErrors = await validateForm();
            const hasErrors =
                validationErrors && Object.keys(validationErrors).length > 0;

            if (hasErrors) {
                if (validationErrors.data) {
                    setSelectedTab('data');
                    return;
                }
                if (validationErrors.roles) {
                    setSelectedTab('roles');
                    return;
                }
                if (validationErrors.reports) {
                    setSelectedTab('reports');
                    return;
                }
            }

            submitForm();
        };

    return (
        <Dialog
            open={isOpen}
            onClose={(_: object, reason?: string) => handleClose(reason)}
            fullScreen={isMobile}
            maxWidth={'lg'}
        >
            {initialValues && (
                <Formik<UserFormValues>
                    initialValues={initialValues}
                    onSubmit={handleSave}
                    enableReinitialize
                    validationSchema={validationSchema}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    {({
                        submitForm,
                        values,
                        setFieldValue,
                        errors,
                        validateForm,
                    }) => {
                        return (
                            <>
                                <DialogTitle
                                    title={title}
                                    close={handleClose}
                                    save={handleSaveClick(
                                        validateForm,
                                        submitForm
                                    )}
                                />
                                <DialogContent
                                    sx={{
                                        minWidth: 800,
                                        height: 800,
                                    }}
                                >
                                    <Box mt={3}>
                                        <CustomToggleButtonGroup
                                            value={selectedTab}
                                            onChange={handleTabChange}
                                            fullWidth
                                            exclusive
                                        >
                                            <ToggleButton value={'data'}>
                                                {TranslationHelper.translate(
                                                    'Data'
                                                )}
                                            </ToggleButton>
                                            <ToggleButton value={'roles'}>
                                                {TranslationHelper.translate(
                                                    'Roles'
                                                )}
                                            </ToggleButton>
                                            {manageReportDefinitionsAssignment && (
                                                <ToggleButton value={'reports'}>
                                                    {TranslationHelper.translate(
                                                        'Reports'
                                                    )}
                                                </ToggleButton>
                                            )}
                                        </CustomToggleButtonGroup>
                                        <Box mt={3}>
                                            {selectedTab === 'data' && (
                                                <DataTab errors={errors.data} />
                                            )}
                                            {selectedTab === 'roles' && (
                                                <RolesTab />
                                            )}
                                            {manageReportDefinitionsAssignment &&
                                                selectedTab === 'reports' && (
                                                    <ReportsTab
                                                        values={values.reports}
                                                        errors={errors.reports}
                                                        onChange={(
                                                            val: UserReportsFormValues
                                                        ) =>
                                                            setFieldValue(
                                                                'reports',
                                                                val
                                                            )
                                                        }
                                                        onReportTypeChange={(
                                                            assignmentId
                                                        ) => {
                                                            setFieldValue(
                                                                `reports.${assignmentId}.templateId`,
                                                                undefined
                                                            );
                                                        }}
                                                    />
                                                )}
                                        </Box>
                                    </Box>
                                </DialogContent>
                            </>
                        );
                    }}
                </Formik>
            )}
        </Dialog>
    );
};
